import { useEffect, useMemo, useRef, useState } from "react"
import { NewsletterSubscribe } from "./NewsletterSubscribe"
import { tv } from "@utils/tw"
import { Button } from "@components/ui/Button"
import { animate, AnimatePresence, motion, useMotionValue, useSpring } from "motion/react"
import { useHydrating, useRouter, useRouterEvents } from "eddev/routing"
import { useNewsletterCookie } from "./useNewsletterCookie"
import { trackEvent } from "@features/tracking/tracking"

const PROMPT_SETTINGS = {
  /** Minimum time on the site before a prompt will be shown */
  minTimeOnSite: 15 * 1000,
  /** Number of pages the user must have visited before the prompt is shown */
  minRouteChanges: 3,
  /** How long to wait after navigating to another page */
  delayAfterRouteChange: 4 * 1000,
  /** If the user has dismissed the popup, prompt them again in this many days */
  promptAgainAfterDays: 3,
}

function useNewsletterPrompt() {
  const [ready, setReady] = useState(false)

  const router = useRouter()
  const newsletterCookie = useNewsletterCookie()

  useEffect(() => {
    if (newsletterCookie.subscribed) {
      setReady(false)
      return
    }
    let isSecondPrompt = false
    if (newsletterCookie.dismissedAt) {
      const tooSoon =
        Date.now() - newsletterCookie.dismissedAt < PROMPT_SETTINGS.promptAgainAfterDays * 24 * 60 * 60 * 1000
      if (tooSoon) {
        setReady(false)
        return
      } else {
        isSecondPrompt = true
      }
    }
    let totalRouteChanges = 0
    let timer: NodeJS.Timeout
    let startTime = Date.now()
    const dispose = router.subscribe((e) => {
      if (e.type === "navigate:changed") {
        totalRouteChanges++
      }
      clearTimeout(timer)
      if (totalRouteChanges >= PROMPT_SETTINGS.minRouteChanges) {
        let minimumPromptTime = PROMPT_SETTINGS.minTimeOnSite - (Date.now() - startTime)
        timer = setTimeout(
          () => {
            setReady(true)
          },
          Math.max(minimumPromptTime, PROMPT_SETTINGS.delayAfterRouteChange),
        )
      }
    })

    return () => {
      dispose()
      clearTimeout(timer)
    }
  }, [newsletterCookie.dismissedAt, newsletterCookie.subscribed])

  return {
    ready,
    newsletterCookie,
  }
}

export function NewsletterPopup() {
  const hydrating = useHydrating()
  const { ready, newsletterCookie } = useNewsletterPrompt()
  const [justDismissed, setJustDismissed] = useState(false)
  const [display, setDisplay] = useState(false)
  const [success, setSuccess] = useState(false)

  const styles = createStyles()

  const sizer = useRef<HTMLDivElement>(null!)
  const contents = useRef<HTMLDivElement>(null!)

  const height = useMotionValue(0)

  useEffect(() => {
    if (justDismissed) {
      setDisplay(false)
      return
    }
    if (ready) {
      setDisplay(true)
      trackEvent(
        "Shown Newsletter Popup",
        {
          IsPromptingAgain: !!newsletterCookie.dismissedAt ? "true" : "false",
        },
        contents.current,
      )
    } else {
      setDisplay(false)
    }
  }, [ready, justDismissed])

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      const value = entries[0].contentRect.height
      if (height.get() === 0) {
        height.set(value)
      } else {
        animate(height, value, {
          type: "spring",
          stiffness: 300,
          damping: 30,
        })
      }
    })
    ro.observe(contents.current!)
    return () => {
      ro.disconnect()
    }
  }, [])

  return (
    <div className={styles.base()} data-mixpanel-area="Newsletter Popup">
      <motion.div
        variants={{
          closed: { y: "120%" },
          open: { y: "0%" },
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        initial="closed"
        animate={display ? "open" : "closed"}
        className={styles.panel()}
      >
        <motion.div ref={sizer} style={{ height: height }}>
          <div ref={contents} className="relative">
            <AnimatePresence initial={false} mode="popLayout">
              <motion.div
                key={success ? "success" : "form"}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="justify-center"
              >
                {success ? (
                  <>
                    <div className="flex items-center justify-between gap-3 type-body-m">
                      <div className={styles.info()}>
                        <h3 className="type-title-m">You're all set</h3>
                        <div className="type-body-m">
                          <p>Thank you for signing up!</p>
                        </div>
                      </div>
                      <Button variant="primary" shape="branded" onClick={() => setSuccess(false)}>
                        Done
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.header()}>
                      <div className={styles.info()}>
                        <h3 className="type-title-m">Don't miss a thing!</h3>
                        <div className="type-body-m">
                          <p>
                            Whether you're in Sydney or one of our rural locations, sign up to our newsletters for all
                            the latest news and offers.
                          </p>
                        </div>
                      </div>
                      <div className={styles.close()}>
                        <Button
                          icon="cross"
                          variant="ghost"
                          onClick={(e) => {
                            setDisplay(false)
                            setJustDismissed(true)
                            newsletterCookie.setDismissed()
                            trackEvent("Dismissed Newsletter Popup", {}, e.currentTarget)
                          }}
                        />
                      </div>
                    </div>
                    <div className={styles.form()}>
                      <NewsletterSubscribe
                        dialog
                        onSuccess={() => setSuccess(true)}
                        onDismiss={() => {
                          setDisplay(false)
                          setJustDismissed(true)
                          trackEvent("Dismissed Newsletter Popup", {}, contents.current)
                        }}
                      />
                    </div>
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </div>
  )
}

const createStyles = tv({
  base: "fixed left-0 sm:left-auto bottom-0 right-0 z-10 theme-dark overflow-hidden pointer-events-none p-4",
  slots: {
    panel: "bg-bg text-fg p-5 rounded-md shadow-popover overflow-hidden pointer-events-auto sm:w-[390px]",
    header: "flex gap-3",
    info: "flex flex-col gap-2",
    close: "flex-none",
    form: "mt-2",
  },
})
